/**
 * ops2026 — Install fleet redeploy (ADR-0239).
 *
 * Admin-only. Shows the current plan (candidate sites with outdated
 * Cloudflare Worker installs), runs a dry-run or live batch deploy,
 * and lists past runs with per-site outcomes.
 *
 * The whole flow is founder-triggered — nothing on this page is
 * scheduled. The dry-run button is safe to press; the live button
 * opens a confirmation dialog and streams the result when the server
 * returns. The server enforces role + batch posture + stop-rule; this
 * view is a thin shell.
 */

import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type {
  AdminInstallFleetPlan,
  AdminInstallFleetRunResult,
  AdminInstallFleetRun,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

type Phase = 'idle' | 'loading' | 'running' | 'ready' | 'error';

export function InstallFleetView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [phase, setPhase] = useState<Phase>('loading');
  const [plan, setPlan] = useState<AdminInstallFleetPlan | null>(null);
  const [result, setResult] = useState<AdminInstallFleetRunResult | null>(null);
  const [runs, setRuns] = useState<readonly AdminInstallFleetRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [batchSize, setBatchSize] = useState<number>(5);
  const [maxSites, setMaxSites] = useState<number>(200);
  const [confirming, setConfirming] = useState(false);

  const loadPlan = useCallback(async (): Promise<void> => {
    setPhase('loading');
    setError(null);
    try {
      const [p, r] = await Promise.all([
        api.getInstallFleetPlan(),
        api.getInstallFleetRuns(),
      ]);
      setPlan(p);
      setRuns(r.runs);
      setPhase('ready');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load plan.');
      setPhase('error');
    }
  }, [api]);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  const run = useCallback(async (dry: boolean): Promise<void> => {
    setPhase('running');
    setError(null);
    try {
      const res = await api.runInstallFleetRedeploy({
        dry_run: dry,
        batch_size: batchSize,
        max_sites: maxSites,
      });
      setResult(res);
      setPhase('ready');
      // Refresh the plan + runs list after a live run.
      if (!dry) void loadPlan();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Run failed.');
      setPhase('error');
    } finally {
      setConfirming(false);
    }
  }, [api, batchSize, maxSites, loadPlan]);

  return (
    <div style={{ padding: 24, maxWidth: 1040 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted }}>
        OPS · INSTALL FLEET
      </div>
      <h1 style={{ fontFamily: serifFont, fontSize: 32, letterSpacing: -1, color: C.text, margin: '4px 0 4px' }}>
        Install fleet redeploy
      </h1>
      <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, maxWidth: 640, lineHeight: 1.5, margin: '0 0 20px' }}>
        Rolls the current Cloudflare Worker template out to merchant sites
        whose observed install version is behind. Admin only. Dry-run shows
        what would change; the live run halts the batch if more than 20% of
        a batch fails verify.
      </p>

      {phase === 'loading' ? (
        <Card>
          <div style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 8, fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            <RefreshCw size={14} style={{ animation: 'spin 1.4s linear infinite' }} />
            Loading plan…
          </div>
        </Card>
      ) : null}

      {phase === 'error' ? (
        <Card>
          <div style={{ padding: 16, color: C.coralFg, fontFamily: sansFont, fontSize: 13 }}>
            {error ?? 'Unknown error.'}
          </div>
        </Card>
      ) : null}

      {plan !== null ? (
        <Card>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text }}>
                {plan.candidate_count} site{plan.candidate_count === 1 ? '' : 's'} outdated
              </div>
              <Pill tone="neutral">latest {plan.install_latest}</Pill>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <label style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                Batch size
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={batchSize}
                  onChange={(e) => setBatchSize(Number.parseInt(e.target.value, 10) || 5)}
                  style={{
                    marginLeft: 6,
                    width: 60,
                    fontFamily: monoFont,
                    fontSize: 12,
                    padding: '4px 6px',
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                    borderRadius: 2,
                  }}
                />
              </label>
              <label style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                Max sites
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={maxSites}
                  onChange={(e) => setMaxSites(Number.parseInt(e.target.value, 10) || 200)}
                  style={{
                    marginLeft: 6,
                    width: 72,
                    fontFamily: monoFont,
                    fontSize: 12,
                    padding: '4px 6px',
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                    borderRadius: 2,
                  }}
                />
              </label>
              <button
                type="button"
                onClick={() => void run(true)}
                disabled={phase === 'running'}
                style={{
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  color: C.text,
                  padding: '6px 12px',
                  fontFamily: sansFont,
                  fontSize: 12,
                  borderRadius: 2,
                  cursor: phase === 'running' ? 'default' : 'pointer',
                  opacity: phase === 'running' ? 0.5 : 1,
                }}
              >
                Dry run
              </button>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={phase === 'running' || plan.candidate_count === 0}
                style={{
                  background: plan.candidate_count === 0 ? C.surface2 : C.amber,
                  border: 'none',
                  color: plan.candidate_count === 0 ? C.muted : C.ink,
                  padding: '6px 12px',
                  fontFamily: sansFont,
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 2,
                  cursor: phase === 'running' || plan.candidate_count === 0 ? 'default' : 'pointer',
                }}
              >
                Deploy fleet…
              </button>
            </div>

            {confirming ? (
              <div style={{
                padding: 12,
                marginBottom: 12,
                background: C.amberDim,
                border: `1px solid ${C.amberFg}`,
                borderRadius: 2,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                  <AlertTriangle size={14} color={C.amberFg} />
                  <span>
                    This writes to {plan.candidate_count} merchant Cloudflare account{plan.candidate_count === 1 ? '' : 's'}.
                    Each site redeploys the current Worker template and verifies the live headers. A batch that fails more than
                    20% halts the whole run.
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => void run(false)}
                    style={{
                      background: C.amber,
                      border: 'none',
                      color: C.ink,
                      padding: '6px 12px',
                      fontFamily: sansFont,
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                  >
                    Confirm deploy
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      color: C.muted,
                      padding: '6px 12px',
                      fontFamily: sansFont,
                      fontSize: 12,
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {plan.sites.length === 0 ? (
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
                The fleet is up to date.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', gap: 6, fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                <div style={{ letterSpacing: 1.2 }}>SITE</div>
                <div style={{ letterSpacing: 1.2 }}>BEFORE</div>
                <div style={{ letterSpacing: 1.2 }}>SITE ID</div>
                {plan.sites.slice(0, 100).map((s) => (
                  <>
                    <div key={`${s.site_id}-d`} style={{ color: C.text, fontFamily: sansFont, fontSize: 12 }}>{s.domain}</div>
                    <div key={`${s.site_id}-b`} style={{ color: C.text, fontFamily: monoFont, fontSize: 11 }}>{s.before_version ?? '—'}</div>
                    <div key={`${s.site_id}-i`} style={{ color: C.muted, fontFamily: monoFont, fontSize: 10 }}>{s.site_id.slice(-10)}</div>
                  </>
                ))}
              </div>
            )}
          </div>
        </Card>
      ) : null}

      {result !== null ? (
        <div style={{ marginTop: 16 }}>
          <Card>
            <div style={{ padding: 16 }}>
              <div style={{ fontFamily: serifFont, fontSize: 20, marginBottom: 8, color: C.text }}>
                {result.dry_run ? 'Dry run result' : 'Run result'}
              </div>
              <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginBottom: 8 }}>
                {result.run_id ?? 'dry-run'} · ok={result.ok_count ?? 0} failed={result.failed_count ?? 0} halted={String(result.halted ?? false)}
                {result.halt_reason !== undefined && result.halt_reason !== null ? ` · ${result.halt_reason}` : ''}
              </div>
              {result.sites.length === 0 ? (
                <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
                  Nothing to deploy.
                </div>
              ) : (
                <div style={{ fontFamily: monoFont, fontSize: 11 }}>
                  {result.sites.slice(0, 100).map((s) => (
                    <div key={s.site_id} style={{ display: 'flex', gap: 8, padding: '4px 0', color: C.text }}>
                      <span style={{ width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {('domain' in s && s.domain !== undefined) ? s.domain : s.site_id.slice(-10)}
                      </span>
                      <span style={{ width: 110, color: outcomeColor(C, 'outcome' in s ? s.outcome : null) }}>
                        {'outcome' in s ? s.outcome : 'planned'}
                      </span>
                      <span style={{ color: C.muted }}>
                        {'reason' in s && s.reason !== null ? s.reason : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      ) : null}

      {runs.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <Card>
            <div style={{ padding: 16 }}>
              <div style={{ fontFamily: serifFont, fontSize: 18, marginBottom: 8, color: C.text }}>
                Past runs
              </div>
              <div style={{ fontFamily: monoFont, fontSize: 11 }}>
                {runs.map((r) => (
                  <div key={r.run_id} style={{ display: 'flex', gap: 12, padding: '4px 0', color: C.text }}>
                    <span style={{ width: 200, color: C.muted }}>{r.started_at}</span>
                    <span style={{ width: 70 }}>{r.dry_run ? 'dry' : 'live'}</span>
                    <span style={{ width: 70, color: r.status === 'ok' ? C.limeFg : r.status === 'halted' ? C.amberFg : C.muted }}>{r.status}</span>
                    <span style={{ width: 100 }}>ok={r.ok_count}</span>
                    <span>failed={r.failed_count}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function outcomeColor(C: ReturnType<typeof useC>, outcome: string | null): string {
  if (outcome === 'ok') return C.limeFg;
  if (outcome === 'verify_failed' || outcome === 'deploy_failed') return C.coralFg;
  if (outcome === 'skipped') return C.muted;
  return C.text;
}
